import { OnChangeFn, SortingState } from "@tanstack/react-table";

import {
  FieldDefinition,
  FieldsOption,
  FilterFormData,
} from "src/base-components/FilterPopover/types";
import { isFieldDefinition } from "src/base-components/FilterPopover/utils";
import { CASES_DEFAULT_SORT } from "src/cases/queries/queues";
import { useCasesPersistedParams } from "src/cases/queues/hooks/casesQueueParamsStore";
import {
  filterFormDataToUrlParams,
  parseSortParam,
  sortingStateToSortParam,
  urlParamsToFilterFormData,
} from "src/cases/queues/hooks/filterParamUtils";
import { isSortableColumnId } from "src/cases/queues/utils";

const SORT_KEY = "sort";

const extractFilterParams = (
  params: Record<string, string>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(params).filter(([key]) => key.startsWith("filter[")),
  );

type UseCasesQueueParamsResult = {
  params: Record<string, string>;
  filterParams: Record<string, string>;
  filterFormData: FilterFormData | undefined;
  hasActiveFilter: boolean;
  applyFilters: (formData: FilterFormData) => void;
  clearFilters: () => void;
  resetParams: () => void;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
};

export const useCasesQueueParams = (
  queueId?: string,
  fields?: FieldsOption[],
): UseCasesQueueParamsResult => {
  const { params, actions } = useCasesPersistedParams({ key: queueId });

  const fieldDefs: FieldDefinition[] = fields?.filter(isFieldDefinition) ?? [];

  const filterFormData =
    fieldDefs.length > 0
      ? urlParamsToFilterFormData(params, fieldDefs)
      : undefined;

  const filterParams = extractFilterParams(params);

  const hasActiveFilter = Object.keys(filterParams).length > 0;

  const sortColumn = parseSortParam(params[SORT_KEY] ?? CASES_DEFAULT_SORT);
  const sorting = [
    isSortableColumnId(sortColumn.id)
      ? sortColumn
      : parseSortParam(CASES_DEFAULT_SORT),
  ];

  const applyFilters = (formData: FilterFormData) => {
    const filterParams = filterFormDataToUrlParams(formData, fieldDefs);
    const sortParam = params[SORT_KEY];
    actions.setParams({
      ...filterParams,
      ...(sortParam ? { [SORT_KEY]: sortParam } : {}),
    });
  };

  const clearFilters = () => {
    const sortParam = params[SORT_KEY];
    if (sortParam) {
      actions.setParams({ [SORT_KEY]: sortParam });
    } else {
      actions.clearParams();
    }
  };

  const resetParams = () => {
    actions.clearParams();
  };

  const onSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    const newSorting =
      typeof updaterOrValue === "function"
        ? updaterOrValue(sorting)
        : updaterOrValue;

    const newSortParam = sortingStateToSortParam(newSorting);
    const filterParams = extractFilterParams(params);

    if (newSortParam) {
      actions.setParams({ ...filterParams, [SORT_KEY]: newSortParam });
    } else {
      actions.setParams(filterParams);
    }
  };

  return {
    params,
    filterParams,
    filterFormData,
    hasActiveFilter,
    applyFilters,
    clearFilters,
    resetParams,
    sorting,
    onSortingChange,
  };
};
